import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Lock, FileCheck2, Loader2, AlertTriangle } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function FideicomisosConfig() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const esAdmin = user?.role === "admin_entidad";
  const [eleg, setEleg] = useState(null);
  const [form, setForm] = useState({ numero_registro_sipres: "", fecha_verificacion: "", registro_condusef_vigente: false, declaracion_87n_aceptada: false });
  const [evidencia, setEvidencia] = useState(null); // {id, nombre_archivo}
  const [subiendo, setSubiendo] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/fideicomisos/elegibilidad");
      setEleg(data);
      setForm({
        numero_registro_sipres: data.numero_registro_sipres || "",
        fecha_verificacion: data.fecha_verificacion || "",
        registro_condusef_vigente: !!data.registro_condusef_vigente,
        declaracion_87n_aceptada: !!data.declaracion_87n_aceptada,
      });
      if (data.evidencia_doc_id) setEvidencia({ id: data.evidencia_doc_id, nombre_archivo: "Evidencia cargada" });
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const subirEvidencia = async (file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo_documento", "Evidencia de registro CONDUSEF (fideicomisos)");
      fd.append("clasificacion", "Fideicomisos");
      fd.append("categoria", "fideicomiso_elegibilidad");
      const { data } = await api.post("/documentos/upload", fd);
      setEvidencia({ id: data.id, nombre_archivo: data.nombre_archivo });
      toast.success(`Evidencia cargada (${data.nombre_archivo}). Sello SHA-256: ${(data.hash_sha256 || "").slice(0, 12)}…`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubiendo(false); }
  };

  const activar = async () => {
    setBusy(true);
    try {
      await api.put("/fideicomisos/elegibilidad", {
        registro_condusef_vigente: form.registro_condusef_vigente,
        numero_registro_sipres: form.numero_registro_sipres,
        fecha_verificacion: form.fecha_verificacion,
        evidencia_doc_id: evidencia?.id || "",
        declaracion_87n_aceptada: form.declaracion_87n_aceptada,
      });
      toast.success("Módulo de Fideicomisos ACTIVADO para tu entidad.");
      navigate("/fideicomisos");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!esAdmin)
    return (
      <div>
        <PageHeader title="Configuración de Fideicomisos" subtitle="Elegibilidad del módulo" />
        <div className="flex flex-col items-center gap-3 p-16 text-slate-400">
          <Lock className="h-10 w-10" />
          <p className="text-sm">Solo el administrador de la entidad configura la elegibilidad.</p>
        </div>
      </div>
    );

  if (!eleg) return <div className="p-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;

  const activo = eleg.estado_modulo === "activo";
  const suspendido = eleg.estado_modulo === "suspendido";

  return (
    <div>
      <PageHeader title="Configuración de Fideicomisos" subtitle="Elegibilidad — registro CONDUSEF + declaración Art. 87-Ñ LGOAAC" />
      <div className="mx-auto max-w-3xl space-y-6 p-6 md:p-8">
        <div className={`flex items-center gap-3 rounded-lg border p-4 ${activo ? "border-emerald-200 bg-emerald-50" : suspendido ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}
          data-testid="fid-config-estado">
          {activo ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : <AlertTriangle className="h-5 w-5 text-amber-600" />}
          <div>
            <p className="text-sm font-semibold text-slate-800">
              Estado del módulo: {activo ? "ACTIVO" : suspendido ? "SUSPENDIDO por la plataforma" : "BLOQUEADO (pendiente de elegibilidad)"}
            </p>
            {suspendido && (
              <p className="text-xs text-slate-600">{eleg.suspension?.motivo ? `Motivo: ${eleg.suspension.motivo}. ` : ""}Solo Felvora puede reactivarlo.</p>
            )}
            {activo && eleg.firmado_fecha && (
              <p className="text-xs text-slate-600">Elegibilidad firmada el {String(eleg.firmado_fecha).slice(0, 10)} · Declaración {eleg.declaracion_87n_texto_version}</p>
            )}
          </div>
        </div>

        {!suspendido && (
          <div className="space-y-5 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Número de registro SIPRES *</label>
                <Input data-testid="fid-config-sipres" placeholder="Ej. 4400000-XX"
                  value={form.numero_registro_sipres}
                  onChange={(e) => setForm((f) => ({ ...f, numero_registro_sipres: e.target.value }))} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Fecha de verificación</label>
                <Input data-testid="fid-config-fecha" type="date" value={form.fecha_verificacion}
                  onChange={(e) => setForm((f) => ({ ...f, fecha_verificacion: e.target.value }))} />
              </div>
            </div>

            <label className="flex items-start gap-2.5 text-sm text-slate-700">
              <Checkbox data-testid="fid-config-condusef" checked={form.registro_condusef_vigente}
                onCheckedChange={(v) => setForm((f) => ({ ...f, registro_condusef_vigente: !!v }))} />
              <span>Declaro que el registro de la entidad ante CONDUSEF (SIPRES) se encuentra <b>vigente</b>.</span>
            </label>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Evidencia del registro (solo PDF) *</label>
              <div className="flex items-center gap-3">
                <input data-testid="fid-config-evidencia" type="file" accept="application/pdf"
                  className="text-xs text-slate-500 file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-white"
                  onChange={(e) => subirEvidencia(e.target.files?.[0])} />
                {subiendo && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                {evidencia && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                    <FileCheck2 className="h-3.5 w-3.5" /> {evidencia.nombre_archivo}
                  </span>
                )}
              </div>
              <p className="mt-1 text-[11px] text-slate-400">Se cifra en reposo (Fernet) con sello SHA-256, vía el expediente digital existente.</p>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Declaración obligatoria — Art. 87-Ñ LGOAAC (versión {eleg.declaracion_87n_version_actual})
              </p>
              <p className="text-xs leading-relaxed text-slate-600">{eleg.declaracion_87n_texto}</p>
              <label className="mt-3 flex items-start gap-2.5 text-sm text-slate-700">
                <Checkbox data-testid="fid-config-87n" checked={form.declaracion_87n_aceptada}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, declaracion_87n_aceptada: !!v }))} />
                <span>Acepto la declaración en nombre de la entidad. Quedará firmada con mi usuario y fecha en bitácora.</span>
              </label>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => navigate("/fideicomisos")}>Volver</Button>
              <Button data-testid="fid-config-activar" onClick={activar}
                disabled={busy || !form.registro_condusef_vigente || !form.declaracion_87n_aceptada || !evidencia || !form.numero_registro_sipres.trim()}>
                {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                {activo ? "Actualizar elegibilidad" : "Activar módulo"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
